import type {AppliedMixinKey} from '@packages/systems/core/utils/StyleTypes';
import type {BreakpointID} from '@packages/systems/style/types';

/**
 * Some workarounds for CSSOM typings and some of our own monkeypatching (prefixed with `__wf_`):
 */

interface HasCSSRuleList {
  cssRules: ReadonlyArray<WebflowCSSRule>;
  deleteRule(index: number): void;
  insertRule(rule: string, index: number): void;
}

/**
 * Actual CSS `@layer` names we emit into the designer stylesheet. Currently
 * just `elements` — authored class rules live outside any layer so they
 * compete with user custom CSS on natural specificity + source order.
 */
export type LayerName = 'elements';
export const ELEMENTS_LAYER = 'elements' as const satisfies LayerName;

/**
 * Sentinel for the "unlayered" pass — rules emitted at the top level of the
 * sheet with no `@layer` wrapper.
 */
export const UNLAYERED = null;
export type UnlayeredSlot = typeof UNLAYERED;

/**
 * An iteration "slot": either unlayered (top level of sheet) or a real
 * `@layer` name.
 */
export type LayerSlot = LayerName | UnlayeredSlot;
export const LAYER_SLOTS: readonly LayerSlot[] = [UNLAYERED, ELEMENTS_LAYER];

export type BreakpointKey = BreakpointID | `${LayerName}--${BreakpointID}`;

export type WebflowCSSRule = CSSRule &
  HasCSSRuleList & {
    style: CSSStyleDeclaration;
    selectorText: string;
    __wf_guid: string;
    __wf_hash: null | string | number;
    __wf_sheet: true;
    __wf_conditionals: WebflowMedias | WebflowContainers;
    __wf_rules: WebflowRules;
    /**
     * The mixins that were expanded into this rule's CSS. Helps us efficiently
     * target any CSS rules that need to be updated when a mixin definition
     * (such as a variable mode) changes.
     *
     * See also:
     *
     * - `AppliedMixinKey`
     * - `StyleSheetUtils`
     * - `compileLess`
     */
    __wf_mixins: Set<AppliedMixinKey>;
  };

export type WebflowCSSMediaRule = Omit<WebflowCSSRule, '__wf_guid'> &
  HasCSSRuleList & {
    mediaText: string;
    __wf_guid: BreakpointKey;
  };

export type WebflowCSSContainerQueryRule = WebflowCSSRule &
  CSSContainerRule & {
    conditionText: string;
  };

export type WebflowMedias = {
  [key: string]: WebflowCSSMediaRule;
};

export type WebflowContainers = {
  [key: string]: WebflowCSSContainerQueryRule;
};

type WebflowRules = {
  [key: string]: WebflowCSSRule;
};

/**
 * A hacky way to detect Safari, since it has funky CSSOM bugs we have to work around.
 */
const IS_SAFARI = (window: (Window & typeof globalThis) | null) =>
  window &&
  Object.prototype.toString.call(window.HTMLElement).indexOf('Constructor') > 0;

/**
 * Modifies a stylesheet to work with our dynamic stylesheet support; may
 * mutate the styleNode; returns a CSSStyleSheet with Webflow properties.
 *
 * @param  {Document}             doc         The HTML document context, typically the canvas iframe's document.
 * @param  {HTMLStyleElement}     styleNode   The unadorned styleSheet element.
 *
 * @return {CSSStyleSheet}        The newly created stylesheet.
 */
export function webflowizeStyleSheet(
  doc: Document,
  styleNode: HTMLStyleElement
): CSSStyleSheet {
  // Safari doesn't recognize the new stylesheet unless it has content
  // Inferred from: https://developer.mozilla.org/en-US/docs/DOM/CSSStyleSheet/insertRule
  if (IS_SAFARI(doc.defaultView)) {
    styleNode.appendChild(doc.createTextNode(''));
  }

  // Grab a reference to the actual CSSStyleSheet CSSOM object
  const newSheet:
    | (CSSStyleSheet & {
        __wf_sheet?: boolean;
        __wf_conditionals?: WebflowMedias | WebflowContainers;
      })
    | null = styleNode.sheet;

  if (newSheet === null) {
    throw new Error(
      'StyleSheet is missing because style node was not inserted into the DOM.'
    );
  }

  // Tag as a Webflow sheet
  newSheet.__wf_sheet = true;

  // IMPORTANT: Keep a map of added media/container queries to prevent Chrome from garbage-collecting them
  // Otherwise, Chrome randomly clones rules and blows away previous expand properties like __wf_guid
  newSheet.__wf_conditionals = {} as WebflowMedias | WebflowContainers;
  return newSheet;
}
